import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useGetLearningModuleQuery, useStartLessonMutation, useCompleteLessonMutation } from '../../store/api/learningApi';
import type { LessonDetail } from '../../store/api/learningApi';
import { lessonPracticeHref } from '../../utils/lessonPracticeLink';
import { LoadErrorNotice } from '../../components/LoadErrorNotice';

/**
 * Why a lesson's start/complete was not saved. 4xx refusals carry a message
 * the learner can act on ("An active workspace is required…", "Lesson not
 * started…"); anything else is a generic failure. Never silent.
 */
export function lessonSaveErrorMessage(error: unknown): string {
  const e = error as { status?: unknown; data?: { message?: unknown } } | null | undefined;
  const status = typeof e?.status === 'number' ? e.status : null;
  const message = e?.data?.message;
  if (status !== null && status >= 400 && status < 500 && status !== 429 && typeof message === 'string' && message.length > 0) {
    return message;
  }
  if (status === 429) return 'BeHeard is busy right now. Please try again shortly.';
  return 'Your lesson progress could not be saved. Please try again.';
}

/**
 * A lesson is available only once it has content (`contentAvailable`, from
 * the server). One without content cannot be started or completed and is not
 * part of the module's progress — it is shown as "Not available yet".
 */
export function lessonIsAvailable(lesson: Pick<LessonDetail, 'contentAvailable'>): boolean {
  return lesson.contentAvailable !== false;
}

/** Module progress over AVAILABLE lessons only (never a completion of an empty lesson). */
export function moduleProgress(lessons: LessonDetail[], completedLessonIds: string[]) {
  const available = lessons.filter(lessonIsAvailable);
  const completed = new Set(completedLessonIds);
  const completedCount = available.filter((l) => completed.has(l.id)).length;
  return {
    availableCount: available.length,
    completedCount,
    percent: available.length > 0 ? Math.round((completedCount / available.length) * 100) : 0,
    nextLessonId: available.find((l) => !completed.has(l.id))?.id,
    firstLessonId: available[0]?.id,
  };
}

export function LearnModulePage() {
  const { category, slug } = useParams<{ category: string; slug: string }>();

  const { data: module, isLoading, error, refetch } = useGetLearningModuleQuery(slug!, {
    skip: !slug,
  });

  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Auto-expand lesson from URL hash on mount
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.startsWith('#lesson-')) {
      setExpandedId(hash.replace('#lesson-', ''));
    }
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-8">
        <nav className="flex items-center gap-2 text-sm text-gray-500">
          <Link to="/learn" className="hover:text-primary-600">Learn</Link>
          <span>/</span>
          <span className="capitalize">{category}</span>
        </nav>
        <div className="text-center py-12 text-gray-500">Loading module...</div>
      </div>
    );
  }

  if (error || !module) {
    const notFound = (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
          <BookIcon className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-gray-500">Module not found</p>
        <Link to="/learn" className="text-primary-600 hover:text-primary-700 mt-2 inline-block">
          Back to Learn
        </Link>
      </div>
    );
    return (
      <div className="space-y-8">
        <nav className="flex items-center gap-2 text-sm text-gray-500">
          <Link to="/learn" className="hover:text-primary-600">Learn</Link>
          <span>/</span>
          <span className="capitalize">{category}</span>
        </nav>
        {/* A 404 is "not found"; a 500 or 429 is a failure/busy with Retry —
            never "not found" for a module the catalog just listed. */}
        {error ? (
          <LoadErrorNotice
            error={error}
            onRetry={() => void refetch()}
            failedMessage="This module could not be loaded."
            notFound={notFound}
            testIdPrefix="learn-module"
          />
        ) : (
          notFound
        )}
      </div>
    );
  }

  const lessons = module.lessonDetails ?? [];
  const completedSet = new Set(module.completedLessonIds ?? []);
  const startedSet = new Set(module.startedLessonIds ?? []);
  const exerciseResponses = module.exerciseResponses ?? {};
  const {
    availableCount,
    completedCount: completedLessons,
    percent: progress,
    nextLessonId,
    firstLessonId,
  } = moduleProgress(lessons, module.completedLessonIds ?? []);

  const toggleLesson = (id: string) => {
    const next = expandedId === id ? null : id;
    setExpandedId(next);
    window.history.replaceState(null, '', next ? `#lesson-${next}` : window.location.pathname);
  };

  const scrollAndExpand = (id: string) => {
    setExpandedId(id);
    window.history.replaceState(null, '', `#lesson-${id}`);
    setTimeout(() => {
      document.getElementById(`lesson-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  return (
    <div className="space-y-8">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500">
        <Link to="/learn" className="hover:text-primary-600">Learn</Link>
        <span>/</span>
        <span className="capitalize">{category}</span>
        <span>/</span>
        <span className="text-gray-900">{module.title}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{module.title}</h1>
          <p className="mt-2 text-gray-500">{module.description}</p>
        </div>

        {/* Progress card */}
        <div className="lg:w-72 card">
          <div className="card-body">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 mb-3">
                <span className="text-2xl font-bold text-primary-600">{progress}%</span>
              </div>
              <p className="text-sm text-gray-500">
                {completedLessons} of {availableCount} lessons complete
              </p>
              {availableCount < lessons.length && (
                <p className="text-xs text-gray-400 mt-1" data-testid="lessons-not-available-note">
                  {lessons.length - availableCount} of {lessons.length} lessons are not available yet
                </p>
              )}
            </div>
            <div className="mt-4 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            {availableCount === 0 ? (
              <p className="mt-4 text-sm text-center text-gray-500" data-testid="module-not-available">
                This module's lessons are not available yet.
              </p>
            ) : progress < 100 ? (
              <button
                className="btn-primary w-full mt-4"
                onClick={() => {
                  if (nextLessonId) {
                    scrollAndExpand(nextLessonId);
                  }
                }}
              >
                {progress > 0 ? 'Continue Learning' : 'Start Module'}
              </button>
            ) : (
              <button
                className="btn-secondary w-full mt-4"
                onClick={() => {
                  if (firstLessonId) scrollAndExpand(firstLessonId);
                }}
              >
                Review Module
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Overview */}
      {module.overview && (
        <div className="card">
          <div className="card-header">
            <h2 className="font-semibold text-gray-900">Overview</h2>
          </div>
          <div className="card-body">
            <p className="text-gray-600">{module.overview}</p>
          </div>
        </div>
      )}

      {/* Lessons list */}
      <div className="card">
        <div className="card-header">
          <h2 className="font-semibold text-gray-900">Lessons</h2>
        </div>
        <div className="divide-y divide-gray-100">
          {lessons.length > 0 ? (
            lessons.map((lesson, index) => (
              <LessonItem
                key={lesson.id}
                lesson={lesson}
                index={index}
                isCompleted={completedSet.has(lesson.id)}
                isStarted={startedSet.has(lesson.id)}
                isExpanded={expandedId === lesson.id}
                onToggle={() => toggleLesson(lesson.id)}
                moduleSlug={slug!}
                exerciseResponse={exerciseResponses[lesson.id]}
              />
            ))
          ) : (
            <div className="card-body text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <BookIcon className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500">
                Lessons are being prepared. Check back soon!
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface LessonItemProps {
  lesson: LessonDetail;
  index: number;
  isCompleted: boolean;
  isStarted: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  moduleSlug: string;
  exerciseResponse?: string;
}

const engageLabels: Record<LessonDetail['type'], string> = {
  video: 'Start Lesson',
  article: 'Mark as Read',
  exercise: 'Submit Exercise',
};

function LessonItem({ lesson, index, isCompleted, isStarted, isExpanded, onToggle, moduleSlug, exerciseResponse }: LessonItemProps) {
  const [startLesson, { isLoading: isStarting, isError: startFailed, error: startError }] = useStartLessonMutation();
  const [completeLesson, { isLoading: isCompleting, isError: completeFailed, error: completeError }] =
    useCompleteLessonMutation();
  // The latest failed save (start or complete), shown until the next attempt.
  const saveError = completeFailed ? completeError : startFailed ? startError : null;
  const [showEngageHint, setShowEngageHint] = useState(false);
  const [exerciseText, setExerciseText] = useState(exerciseResponse ?? '');

  // No content: nothing to start, nothing to complete.
  const isAvailable = lessonIsAvailable(lesson);

  // Engaged = backend says started OR completed (persisted across refresh)
  const isEngaged = isAvailable && (isStarted || isCompleted);

  // Clear hint once engaged
  useEffect(() => {
    if (isEngaged) setShowEngageHint(false);
  }, [isEngaged]);

  const typeIcons = {
    video: <PlayIcon className="w-4 h-4" />,
    article: <DocumentIcon className="w-4 h-4" />,
    exercise: <PencilIcon className="w-4 h-4" />,
  };

  const typeLabels = {
    video: 'Video',
    article: 'Article',
    exercise: 'Exercise',
  };

  const isLoading = isStarting || isCompleting;
  const canComplete = isAvailable && (isEngaged || isCompleted);
  const practiceHref = lessonPracticeHref(lesson.practice);

  const handleComplete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAvailable || isCompleted || isLoading) return;
    if (!isEngaged) {
      setShowEngageHint(true);
      return;
    }
    completeLesson({ slug: moduleSlug, lessonId: lesson.id });
  };

  const handleEngage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAvailable) return;
    if (lesson.type === 'exercise') {
      startLesson({ slug: moduleSlug, lessonId: lesson.id, response: exerciseText });
    } else {
      startLesson({ slug: moduleSlug, lessonId: lesson.id });
    }
  };

  return (
    <div id={`lesson-${lesson.id}`}>
      {/* Clickable row */}
      <div
        className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
        onClick={onToggle}
      >
        {/* Lesson number / check */}
        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${isCompleted ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}>
          {isCompleted ? <CheckIcon className="w-4 h-4" /> : index + 1}
        </div>

        {/* Lesson info */}
        <div className="flex-1 min-w-0">
          <h3 className={`font-medium ${isCompleted ? 'text-gray-500' : 'text-gray-900'}`}>
            {lesson.title}
          </h3>
          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              {typeIcons[lesson.type]}
              {typeLabels[lesson.type]}
            </span>
            <span>{lesson.duration}</span>
          </div>
        </div>

        {/* Chevron */}
        <ChevronIcon className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />

        {/* Action — a lesson without content shows that, never a Complete button */}
        {!isAvailable ? (
          <span
            className="px-3 py-2 rounded-lg text-xs font-medium bg-gray-100 text-gray-500"
            data-testid="lesson-not-available"
          >
            Not available yet
          </span>
        ) : (
        <button
          onClick={handleComplete}
          disabled={isLoading || !canComplete}
          title={canComplete ? undefined : 'Complete the lesson task first.'}
          aria-label={canComplete ? `Complete ${lesson.title}` : `Cannot complete ${lesson.title} — finish the lesson task first`}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isCompleted ? 'bg-green-50 text-green-700' : canComplete ? 'bg-primary-600 text-white hover:bg-primary-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
        >
          {isLoading ? 'Saving...' : isCompleted ? 'Done' : 'Complete'}
        </button>
        )}
      </div>

      {/* Save failure (start/complete): said, never silent */}
      {saveError && (
        <div className="px-6 pb-2" role="alert" data-testid="lesson-save-error">
          <p className="ml-12 text-xs text-red-600">{lessonSaveErrorMessage(saveError)}</p>
        </div>
      )}

      {/* Engage hint */}
      {showEngageHint && !isEngaged && (
        <div className="px-6 pb-2" role="status" aria-live="polite">
          <p className="ml-12 text-xs text-amber-600">Complete the lesson task before marking it done.</p>
        </div>
      )}

      {/* Expanded: a lesson without content says so (nothing to start or complete) */}
      {isExpanded && !isAvailable && (
        <div className="px-6 pb-4">
          <div className="ml-12 p-4 bg-gray-50 rounded-lg text-sm text-gray-600 space-y-3">
            <p data-testid="lesson-not-available-panel">
              This lesson is not available yet. It does not count toward this module's progress.
            </p>
            {practiceHref && (
              <Link
                to={practiceHref}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium border border-primary-600 text-primary-700 hover:bg-primary-50 transition-colors"
                data-testid="lesson-practice-link"
              >
                Practice this
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Expanded content panel */}
      {isExpanded && isAvailable && (
        <div className="px-6 pb-4">
          <div className="ml-12 p-4 bg-gray-50 rounded-lg text-sm text-gray-700 leading-relaxed space-y-3">
            {/* ===== VIDEO LESSON ===== */}
            {lesson.type === 'video' && (
              <>
                {lesson.contentUrl && isEmbeddableVideo(lesson.contentUrl) ? (
                  <div className="aspect-video rounded-lg overflow-hidden bg-black">
                    <iframe
                      src={toEmbedUrl(lesson.contentUrl)}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={lesson.title}
                    />
                  </div>
                ) : lesson.contentUrl ? (
                  <a
                    href={lesson.contentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-3 rounded-lg bg-primary-50 text-primary-700 hover:bg-primary-100 font-medium transition-colors"
                  >
                    <PlayIcon className="w-5 h-5" />
                    Watch Video
                    <ExternalLinkIcon className="w-4 h-4" />
                  </a>
                ) : (
                  <p className="text-gray-400 italic">This lesson's video is not available yet.</p>
                )}
                {lesson.markdown && (
                  <p className="whitespace-pre-wrap">{lesson.markdown}</p>
                )}
              </>
            )}

            {/* ===== ARTICLE LESSON ===== */}
            {lesson.type === 'article' && (
              <>
                {lesson.markdown && (
                  <div className="whitespace-pre-wrap">{lesson.markdown}</div>
                )}
                {lesson.contentUrl && (
                  <a
                    href={lesson.contentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-3 rounded-lg bg-primary-50 text-primary-700 hover:bg-primary-100 font-medium transition-colors"
                  >
                    <DocumentIcon className="w-5 h-5" />
                    Read Article
                    <ExternalLinkIcon className="w-4 h-4" />
                  </a>
                )}
                {!lesson.contentUrl && !lesson.markdown && (
                  <p className="text-gray-400 italic">This article is not available yet.</p>
                )}
              </>
            )}

            {/* ===== EXERCISE LESSON ===== */}
            {lesson.type === 'exercise' && (
              <>
                {lesson.markdown && (
                  <div className="whitespace-pre-wrap font-medium text-gray-800">{lesson.markdown}</div>
                )}
                {!lesson.markdown && (
                  <p className="text-gray-600">Write your response below:</p>
                )}
                <textarea
                  value={exerciseText}
                  onChange={(e) => setExerciseText(e.target.value)}
                  placeholder="Type your response here..."
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y"
                  disabled={isCompleted}
                />
                {isEngaged && exerciseResponse && (
                  <p className="text-xs text-green-600 font-medium">Response submitted</p>
                )}
              </>
            )}

            {/* ===== ENGAGE / STATUS BUTTONS ===== */}
            {!isEngaged && !isCompleted && (
              <button
                onClick={handleEngage}
                disabled={isStarting || (lesson.type === 'exercise' && exerciseText.trim().length === 0)}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {isStarting ? 'Starting...' : engageLabels[lesson.type]}
              </button>
            )}
            {isEngaged && !isCompleted && lesson.type !== 'exercise' && (
              <p className="text-xs font-medium text-green-600">Ready to complete</p>
            )}
            {isEngaged && !isCompleted && lesson.type === 'exercise' && (
              <button
                onClick={handleEngage}
                disabled={isStarting || exerciseText.trim().length === 0}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {isStarting ? 'Saving...' : 'Update Response'}
              </button>
            )}

            {/* ===== LEARN → PRACTICE (only when the lesson authors a link) ===== */}
            {practiceHref && (
              <Link
                to={practiceHref}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium border border-primary-600 text-primary-700 hover:bg-primary-50 transition-colors"
                data-testid="lesson-practice-link"
              >
                Practice this
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Detects YouTube or Vimeo URLs that can be embedded */
function isEmbeddableVideo(url: string): boolean {
  return /youtube\.com\/watch|youtu\.be\/|vimeo\.com\/\d/.test(url);
}

/** Converts a YouTube/Vimeo watch URL to an embed URL */
function toEmbedUrl(url: string): string {
  // YouTube: https://www.youtube.com/watch?v=ID or https://youtu.be/ID
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  // Vimeo: https://vimeo.com/ID
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return url;
}

// Icon components
function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function DocumentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function ExternalLinkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
      />
    </svg>
  );
}

function BookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
      />
    </svg>
  );
}
